import React from 'react';
import {Card, CardContent, Grid, Stack, Typography} from '@mui/material';
import {Trans} from '@lingui/react/macro';
import {useGetGroupQuery as useGetLogGroupQuery} from '../../../apis/Log.api.ts';
import {useGetGroupQuery as useGetCordwoodGroupQuery} from '../../../apis/Cordwood.api.ts';
import GradientCircularProgress from '../../../../share/components/progress/GradientCircularProgress.tsx';
import {fixedFormat, percentageFormat} from '../../../../utils/numberFormat.ts';
import {ID} from '../../../../types/global.d';

type KpiTileProps = {
    label: React.ReactNode;
    value: React.ReactNode;
    hint?: React.ReactNode;
    testId?: string;
};

const KpiTile = ({label, value, hint, testId}: KpiTileProps) => (
    <Card variant="outlined" sx={{height: '100%'}} data-testid={testId}>
        <CardContent sx={{padding: '1em', '&:last-child': {paddingBottom: '1em'}}}>
            <Typography
                variant="overline"
                color="text.secondary"
                sx={{display: 'block', lineHeight: 1.4}}
            >
                {label}
            </Typography>
            <Typography variant="h5" component="div" sx={{marginTop: '0.25em'}}>
                {value}
            </Typography>
            {hint &&
            <Typography variant="caption" color="text.secondary" component="div">
                {hint}
            </Typography>
            }
        </CardContent>
    </Card>
);

const sumVolume = (rows: any[] | undefined) =>
    (rows || []).reduce((acc, row) => acc + (row?.totalVolume || 0), 0);

const sumCount = (rows: any[] | undefined) =>
    (rows || []).reduce((acc, row) => acc + (row?.totalCount || 0), 0);

type ProviderSummaryCardsProps = {
    providerId: ID;
    totalCuttings?: number;
    activeCuttings?: number;
};

/**
 * The provider at a glance: production volume, how much of it left the yard and
 * how much is still standing in the storages.
 *
 * Every number comes from the group-by endpoints, scoped to the provider. The
 * backend resolves the provider through the cutting and restricts the rows to
 * the caller's scope group, so a provider only ever sees its own timber.
 */
const ProviderSummaryCards = ({providerId, totalCuttings = 0, activeCuttings = 0}: ProviderSummaryCardsProps) => {

    const providerIdRefs = React.useMemo(() => (providerId ? [providerId] : []), [providerId]);
    const hasProvider = providerIdRefs.length > 0;

    // The group endpoints take a flat filter body, so the arguments are passed
    // flat on purpose - the `{ list, filter }` shape of the generated types
    // does not match what the endpoint reads.
    const {data: logsAll, isFetching: logsAllFetching} = useGetLogGroupQuery(
        {providerId: providerIdRefs} as any,
        {skip: !hasProvider}
    );
    const {data: logsShipped, isFetching: logsShippedFetching} = useGetLogGroupQuery(
        {providerId: providerIdRefs, shipmentDefined: true} as any,
        {skip: !hasProvider}
    );
    const {data: logsNotShipped, isFetching: logsNotShippedFetching} = useGetLogGroupQuery(
        {providerId: providerIdRefs, shipmentDefined: false} as any,
        {skip: !hasProvider}
    );
    const {data: cordwoodAll, isFetching: cordwoodAllFetching} = useGetCordwoodGroupQuery(
        {providerId: providerIdRefs} as any,
        {skip: !hasProvider}
    );
    const {data: cordwoodShipped, isFetching: cordwoodShippedFetching} = useGetCordwoodGroupQuery(
        {providerId: providerIdRefs, shipmentDefined: true} as any,
        {skip: !hasProvider}
    );
    const {data: cordwoodNotShipped, isFetching: cordwoodNotShippedFetching} = useGetCordwoodGroupQuery(
        {providerId: providerIdRefs, shipmentDefined: false} as any,
        {skip: !hasProvider}
    );

    const loading = logsAllFetching || logsShippedFetching || logsNotShippedFetching
        || cordwoodAllFetching || cordwoodShippedFetching || cordwoodNotShippedFetching;

    const logsVolume = sumVolume(logsAll);
    const logsCount = sumCount(logsAll);
    const logsShippedVolume = sumVolume(logsShipped);
    const logsStockVolume = sumVolume(logsNotShipped);

    const cordwoodVolume = sumVolume(cordwoodAll);
    const cordwoodShippedVolume = sumVolume(cordwoodShipped);
    const cordwoodStockVolume = sumVolume(cordwoodNotShipped);

    const totalVolume = logsVolume + cordwoodVolume;
    const shippedVolume = logsShippedVolume + cordwoodShippedVolume;
    const stockVolume = logsStockVolume + cordwoodStockVolume;
    const shippedRatio = totalVolume > 0 ? shippedVolume / totalVolume : 0;

    if (loading && !logsAll && !cordwoodAll) {
        return (
            <Stack sx={{alignItems: 'center', padding: '2em'}} data-testid="Provider-summary-loading">
                <GradientCircularProgress/>
            </Stack>
        );
    }

    return (
        <Grid container spacing={2} data-testid="Provider-summary-cards">
            <Grid size={{xs: 6, md: 3}}>
                <KpiTile
                    testId="Provider-summary-active-cuttings"
                    label={<Trans>Active cuttings</Trans>}
                    value={activeCuttings}
                    hint={<Trans>{`of ${totalCuttings} total`}</Trans>}
                />
            </Grid>
            <Grid size={{xs: 6, md: 3}}>
                <KpiTile
                    testId="Provider-summary-volume"
                    label={<Trans>Timber volume (m³)</Trans>}
                    value={fixedFormat(totalVolume)}
                    hint={<Trans>logs and cordwood</Trans>}
                />
            </Grid>
            <Grid size={{xs: 6, md: 3}}>
                <KpiTile
                    testId="Provider-summary-shipped-volume"
                    label={<Trans>Shipped volume (m³)</Trans>}
                    value={fixedFormat(shippedVolume)}
                    hint={<Trans>left the storage</Trans>}
                />
            </Grid>
            <Grid size={{xs: 6, md: 3}}>
                <KpiTile
                    testId="Provider-summary-stock-volume"
                    label={<Trans>In storage (m³)</Trans>}
                    value={fixedFormat(stockVolume)}
                    hint={<Trans>not shipped yet</Trans>}
                />
            </Grid>
            <Grid size={{xs: 6, md: 3}}>
                <KpiTile
                    testId="Provider-summary-shipped-ratio"
                    label={<Trans>Shipped</Trans>}
                    value={percentageFormat(shippedRatio)}
                    hint={<Trans>of the produced volume</Trans>}
                />
            </Grid>
            <Grid size={{xs: 6, md: 3}}>
                <KpiTile
                    testId="Provider-summary-logs"
                    label={<Trans>Logs</Trans>}
                    value={logsCount}
                    hint={<Trans>pieces</Trans>}
                />
            </Grid>
            <Grid size={{xs: 6, md: 3}}>
                <KpiTile
                    testId="Provider-summary-logs-volume"
                    label={<Trans>Logs volume (m³)</Trans>}
                    value={fixedFormat(logsVolume)}
                    hint={<Trans>shipped {fixedFormat(logsShippedVolume)}</Trans>}
                />
            </Grid>
            <Grid size={{xs: 6, md: 3}}>
                <KpiTile
                    testId="Provider-summary-cordwood-volume"
                    label={<Trans>Cordwood volume (m³)</Trans>}
                    value={fixedFormat(cordwoodVolume)}
                    hint={<Trans>shipped {fixedFormat(cordwoodShippedVolume)}</Trans>}
                />
            </Grid>
        </Grid>
    );
};

export default ProviderSummaryCards;
